import {
  getWishlist as fetchWishlistApi,
  addToWishlist as addWishlistItemApi,
  removeFromWishlist as removeWishlistItemApi,
} from '../api/wishlist';

const WISHLIST_STORAGE_KEY = 'wishlist';

const safeParse = (value, fallback = []) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    console.error('Failed to parse wishlist from storage', error);
    return fallback;
  }
};

const saveLocalWishlist = (items) => {
  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Failed to save wishlist to storage', error);
  }
};

const loadLocalWishlist = () => {
  if (typeof window === 'undefined') {
    return [];
  }
  return safeParse(localStorage.getItem(WISHLIST_STORAGE_KEY));
};

const normalizeWishlistProduct = (product) => {
  if (!product) {
    throw new Error('Invalid product reference');
  }

  return {
    id: product.id || product.product_id || product.productId,
    title: product.title || product.name || product.translations?.[0]?.name || 'Product',
    price:
      product.price ||
      product.formatted_price ||
      product.selling_price?.amount ||
      product.selling_price?.formatted ||
      product.price?.amount ||
      product.price?.formatted ||
      '0.00',
    image: product.image || product.base_image?.path || product.media?.[0]?.path || '/placeholder.jpg',
  };
};

const addLocalWishlistItem = (product) => {
  const normalized = normalizeWishlistProduct(product);
  if (!normalized.id) {
    throw new Error('Product ID is required for wishlist');
  }
  const items = loadLocalWishlist();
  const exists = items.some((item) => item.id === normalized.id);
  if (!exists) {
    items.push(normalized);
    saveLocalWishlist(items);
  }
  return items;
};

const removeLocalWishlistItem = (productId) => {
  const items = loadLocalWishlist().filter((item) => item.id !== productId);
  saveLocalWishlist(items);
  return items;
};

const clearLocalWishlist = () => {
  try {
    localStorage.removeItem(WISHLIST_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear wishlist storage', error);
  }
};

const fetchRemoteWishlist = async (userId) => {
  const response = await fetchWishlistApi(userId);
  return response || [];
};

const addRemoteWishlistItem = async (userId, productId) => {
  await addWishlistItemApi({ user_id: userId, product_id: productId });
  return fetchRemoteWishlist(userId);
};

const removeRemoteWishlistItem = async (userId, productId) => {
  await removeWishlistItemApi({ user_id: userId, product_id: productId });
  return fetchRemoteWishlist(userId);
};

const syncLocalWishlistToRemote = async (userId) => {
  if (!userId) return loadLocalWishlist();

  const localItems = loadLocalWishlist();
  if (!localItems.length) {
    return fetchRemoteWishlist(userId);
  }

  for (const item of localItems) {
    try {
      await addWishlistItemApi({ user_id: userId, product_id: item.id });
    } catch (error) {
      console.error('Failed to sync wishlist item', item.id, error);
    }
  }

  clearLocalWishlist();
  return fetchRemoteWishlist(userId);
};

const wishlistService = {
  loadLocal: loadLocalWishlist,
  saveLocal: saveLocalWishlist,
  addLocal: addLocalWishlistItem,
  removeLocal: removeLocalWishlistItem,
  clearLocal: clearLocalWishlist,
  fetchRemote: fetchRemoteWishlist,
  addRemote: addRemoteWishlistItem,
  removeRemote: removeRemoteWishlistItem,
  syncLocalToRemote: syncLocalWishlistToRemote,
};

export default wishlistService;

